import { useBroadcastChannel } from '@vueuse/core';
import { watch } from 'vue';

import router from '@/router';

import { type LoginFlowData, useAuth } from "@/stores/auth";

export type CrossTabAuthEvent = 'auth:success' | 'auth:logout';

export interface CrossTabAuthMessage {
    type: CrossTabAuthEvent;
    timestamp: number;
    loginFlowData?: LoginFlowData;
}

/**
 * Composable for cross-tab authentication event broadcasting
 * Uses VueUse's useBroadcastChannel to sync authentication events across tabs
 */
export function useCrossTabAuth() {
    const auth = useAuth();

    const {
        isSupported,
        data,
        post,
        close,
    } = useBroadcastChannel<CrossTabAuthMessage, CrossTabAuthMessage>({
        name: 'tellscale-portal-auth',
    });

    /**
     * Broadcast authentication success to all other tabs
     *
     * @param loginFlowData {LoginFlowData} - The login flow email associated with the authentication
     */
    const broadcastAuthSuccess = (loginFlowData: LoginFlowData): void => {
        if(!isSupported.value) {
            console.warn('[CrossTabAuth] BroadcastChannel not supported in this browser');
            return;
        }

        const message: CrossTabAuthMessage = {
            type: 'auth:success',
            timestamp: Date.now(),
            loginFlowData: loginFlowData,
        };

        post(message);

        console.info('[CrossTabAuth] Broadcasted auth:success');
    };

    /**
     * Broadcast logout event to all other tabs
     */
    const broadcastLogout = (): void => {
        if(!isSupported.value) {
            console.warn('[CrossTabAuth] BroadcastChannel not supported in this browser');
            return;
        }

        const message: CrossTabAuthMessage = {
            type: 'auth:logout',
            timestamp: Date.now(),
        };

        post(message);

        console.info('[CrossTabAuth] Broadcasted auth:logout');
    };

    /**
     * Handle incoming authentication events from other tabs
     *
     * @param message {CrossTabAuthMessage | undefined} - The received message
     */
    const handleAuthEvent = async (message: CrossTabAuthMessage | undefined): Promise<void> => {
        if(!message) return;

        console.info(`[CrossTabAuth] Received event: ${message.type}`);

        switch(message.type) {
            case 'auth:success': return await handleAuthSuccessEvent(message);
            case 'auth:logout': return await handleLogoutEvent();
        }
    };

    const handleAuthSuccessEvent = async (message: CrossTabAuthMessage) => {
        if(auth.isAuthenticated) return;

        await auth.authenticate(true);

        if(message.loginFlowData) {
            auth.loginFlowData = message.loginFlowData;
        }

        console.info('[CrossTabAuth] Redirecting to login success page');

        await router.replace('/auth/success');

        auth.loginFlowData = undefined;
    }

    const handleLogoutEvent = async () => {
        if(!auth.isAuthenticated) return;

        console.info('[CrossTabAuth] Logout event received from another tab');

        await auth.logout(true);

        await router.replace('/portal/login');
    }

    // Watch for incoming messages
    watch(data, handleAuthEvent);

    return {
        isSupported,
        broadcastAuthSuccess,
        broadcastLogout,
        close,
    };
}
